<template>
  <v-menu v-if="isAuthenticated" :nudge-bottom="60" offset-x>
    <template #activator="{ on }">
      <v-btn icon large data-qa="bm-profile-avatar-button" v-on="on">
        <v-avatar color="white" size="40">
          <span class="black--text">{{ userInitials }}</span>
        </v-avatar>
      </v-btn>
    </template>

    <v-card class="scrollable-card">
      <v-list class="profile-avatar-menu">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title>{{ user.name }}</v-list-item-title>
            <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-content>
            <bm-btn
              button-type="secondary"
              :to="brewmanNewsRoute"
              :t="$m.keys.news_brewman_news"
            />
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="hasAdministrativePrivileges">
          <v-list-item-content>
            <bm-btn
              button-type="secondary"
              :to="billingRoute"
              :t="$m.keys.billing_your_subscription"
            />
          </v-list-item-content>
        </v-list-item>
        <v-list-item
          v-if="
            currentTenant?.locationConfiguration.locationCountryCode === 'GB'
          "
        >
          <v-list-item-content>
            <bm-btn
              button-type="secondary"
              :to="labelOrderingRoute"
              :t="$m.keys.label_order"
            />
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="screenSize.smAndDown">
          <v-list-item-content>
            <bm-btn-dropdown
              button-type="secondary"
              :items="languageDropdownMenuItems"
            >
              <div class="text-truncate">
                {{
                  translateWithParams("avatar_language", {
                    language: currentLanguageLocale.nativeDescription,
                  })
                }}
              </div>
            </bm-btn-dropdown>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-content>
            <bm-switch
              :value="theme.isDark()"
              :t="$m.keys.avatar_dark_theme"
              @update:value="toggleDarkTheme"
            />
          </v-list-item-content>
        </v-list-item>
        <v-divider />
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title>{{ $m.avatar_useful_links }}</v-list-item-title>
            <v-container pa-0 mt-2 fluid>
              <v-row dense>
                <v-col>
                  <a
                    :href="resolvedHelpLink"
                    class="external-link"
                    target="_blank"
                  >
                    {{ $m.avatar_help_pages }}
                    <bm-icon icon="Launch" />
                  </a>
                </v-col>
              </v-row>
            </v-container>
          </v-list-item-content>
        </v-list-item>
        <v-divider />
        <v-list-item v-if="currentTenant">
          <v-list-item-content>
            {{ $m.avatar_current_company }}
            <bm-radio-group
              class="slim-control"
              :value="currentTenant.id.toString()"
              @update:value="changeTenant"
            >
              <bm-radio
                v-for="tenant in allAvailableTenants"
                :key="tenant.id.toString()"
                :label="tenant.name"
                :value="tenant.id.toString()"
                :ripple="false"
              />
            </bm-radio-group>
          </v-list-item-content>
        </v-list-item>
        <v-list-item
          v-if="
            allAvailableTenantGroups && currentTenantGroup && currentGipUser
          "
        >
          <v-list-item-content>
            {{ $m.avatar_other_companies }}
            <bm-radio-group
              class="slim-control"
              :value="currentTenantGroup.id.toString()"
              @update:value="switchTenantGroup"
            >
              <bm-radio
                v-for="tenantGroup in allAvailableTenantGroups"
                :key="tenantGroup.id.toString()"
                :label="tenantGroup.name"
                :value="tenantGroup.id.toString()"
                :ripple="false"
              />
            </bm-radio-group>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-list v-if="isPremierOrSupportUser" class="bm-body">
        <v-divider />
        <v-list-item>
          <v-list-item-content>
            <bm-form-flow-layout cols="6">
              <bm-text
                label="Tenant Group Id"
                :value="user.readOnly.tenantGroupId.toString()"
                read-only
                show-copy-clipboard-action-simple
              />
              <bm-text
                label="Tenant Group Name"
                :value="currentTenantGroup && currentTenantGroup.name"
                read-only
                show-copy-clipboard-action-simple
              />
              <bm-text
                label="Tenant Id"
                :value="currentTenant && currentTenant.id.toString()"
                read-only
                show-copy-clipboard-action-simple
              />
              <bm-text
                label="Tenant Name"
                :value="currentTenant && currentTenant.name"
                read-only
                show-copy-clipboard-action-simple
              />

              <bm-text
                label="Route Name"
                :value="$route.name"
                read-only
                show-copy-clipboard-action-simple
              />

              <bm-text
                label="Route Helplink Key"
                :value="'helplink_route_' + $route.name"
                read-only
                show-copy-clipboard-action-simple
              />

              <bm-text
                label="Route Current Helplink"
                :value="resolvedHelpLink"
                read-only
                show-copy-clipboard-action-simple
              />
            </bm-form-flow-layout>
            <a
              class="external-link mt-2"
              target="_blank"
              :href="supportTranslationsUrl"
              >Link to Translations Document <bm-icon icon="Launch"
            /></a>
            <a
              class="external-link mt-2"
              target="_blank"
              :href="supportLoggingUrl"
              >Link to Logs <bm-icon icon="Launch"
            /></a>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-divider />

      <v-card-actions>
        <v-container>
          <v-row>
            <v-col class="pa-0 text-center">
              <bm-btn
                button-type="ghost"
                block
                :t="$m.keys.auth_log_out"
                @click="logOut"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import { computed } from "vue";
import userStore from "@/store/modules/userStore";
import type { User } from "@/app/services/generated/userService";
import { withLoading } from "@/components/composables/componentTools";
import Guid from "@/app/types/common/guid";
import environment from "@/environment";
import type { Tenant } from "@/app/services/generated/tenantService";
import appStore from "@/store/modules/appStore";
import BmFormFlowLayout from "@/components/controls/BmFormFlowLayout.vue";
import { useAuthentication } from "@/components/pages/account/composables/authentication";
import BmBtnDropdown from "@/components/controls/input/BmBtnDropdown.vue";
import type { BmBtnDropdownMenuItem } from "@/components/controls/input/types/BmBtnDropdownTypes";
import { useLocaleSelection } from "@/locales/useLocaleSelection";
import { translateWithParams } from "@/plugins/i18n";
import { adminRoutes } from "@/app/ui/navigation/administrationMenu";
import { theme } from "@/app/theme";
import { mainRoutes } from "@/app/ui/navigation/mainMenu";
import { screenSize } from "@/vue23switching/screenSize";

const { currentGipUser } = useAuthentication();
const {
  availableLanguageLocales,
  currentLanguageLocale,
  setLanguage,
  localeToFlagCss,
} = useLocaleSelection();

const toggleDarkTheme = (darkTheme: boolean) => theme.setMode(darkTheme);

const allAvailableTenants = computed((): readonly Tenant[] => {
  return userStore.state.allAvailableTenants as readonly Tenant[];
});

const switchTenantGroup = withLoading(async (tenantGroupId: Guid) => {
  if (currentGipUser.value) {
    await userStore.switchTenantGroupAndRefresh(tenantGroupId);
  }
});

const languageDropdownMenuItems = computed((): BmBtnDropdownMenuItem[] =>
  availableLanguageLocales.value.map((locale) => ({
    text: locale.nativeDescription,
    item: locale.langLocale,
    iconConfig: {
      width: "20px",
      height: "24px",
      class: localeToFlagCss(locale),
    },
    action: (_, item) => setLanguage(item),
  }))
);

const isPremierOrSupportUser = userStore.isPremierOrSupportUser;
const resolvedHelpLink = appStore.resolvedHelpLink;
const billingRoute = adminRoutes.billingDetails.location();
const labelOrderingRoute = adminRoutes.labelOrders.location();
const brewmanNewsRoute = mainRoutes.brewmanNews.location();
const hasAdministrativePrivileges = computed(
  () => userStore.hasAdministrativePrivileges.value
);
const isAuthenticated = computed(
  () => userStore.isAuthenticatedWithSelectedTenant.value
);
const user = computed((): Readonly<User> => {
  if (!userStore.state.user) {
    throw Error("Null User");
  }

  return userStore.state.user as Readonly<User>;
});
const currentTenant = computed(() => userStore.state.tenant);
const userInitials = computed(() => userStore.userInitials.value);
const changeTenant = withLoading(async (tenantId: string) => {
  const tenantIdGuid = Guid.tryParse(tenantId);
  if (tenantIdGuid) {
    await userStore.switchTenantAndRefresh(tenantIdGuid);
  }
});
const logOut = async () => await userStore.signOut("avatar logout click");
const allAvailableTenantGroups = computed(
  () => userStore.state.allAvailableTenantGroups
);
const currentTenantGroup = computed(() => userStore.state.tenantGroup);
const supportTranslationsUrl = environment.supportTranslationsUrl;
const supportLoggingUrl = environment.supportLoggingUrl;
</script>

<style lang="sass" scoped>
@import "src/sass/colours"
@import "src/sass/typography"

.external-link
  text-decoration: none
  color: $bm-color-text01
  @include bm-body

.profile-avatar-menu
  max-height: 500px
  overflow-y: auto

.scrollable-card
  max-height: calc(100vh - 40px)
  overflow-y: auto
</style>
